// SettingsMain.tsx
import { useSearchParams } from 'react-router-dom';
import { useMemo, useEffect } from 'react';
import ApiIntegration from './ApiIntegration';
import ConfigurationSettings from './ConfigurationSettings';
import { Settings, Wrench, CalendarDays, Tags, Workflow, Users, UserCog, FileText, Activity, Building2, Database } from 'lucide-react';
import TagsSettings from './TagsSettings';
import ServicesMonitoring from './ServicesMonitoring';
import DbSettings from './DbSettings';
import { hasPermission } from '../../utils/permissionsHelper';
// import Jobs from './Scheduler/Jobs';
import Schedules from './Scheduler/Schedules';
import { SettingsSidebar } from './SettingsSidebar';
import Roles from '../Roles';
import User from '../User';
import EventTemplates from './EventTemplates';
import ConfigurationDetails from './ConfigurationDetails';
import { useClientLevel } from '../../hooks/useClientLevel';
// import RuleEngine from './RuleEngine/RuleEngine';
import OrganizationSettings from './OrganizationSettings';
// import CustomScripts from './CustomScripts';

export default function SettingsMain() {
    const [searchParams, setSearchParams] = useSearchParams();

    const BASE_TABS = [
        { id: 'organization_settings', label: 'Organization', component: <OrganizationSettings />, permissionKey: null, icon: <Building2 size={16} /> },
        { id: 'api_integration', label: 'Integrations', component: <ApiIntegration />, permissionKey: 'integrations', icon: <Workflow size={16} /> },
        { id: 'event_templates', label: 'Templates', component: <EventTemplates />, permissionKey: 'events', icon: <FileText size={16} /> },
        // { id: 'custom_scripts', label: 'Scripts', component: <CustomScripts />, permissionKey: 'custom_scripts', icon: <Terminal size={16} /> },
        // { id: 'rule_engine', label: 'Rule Engine', component: <RuleEngine />, permissionKey: null, icon: <Brain size={16} /> },
        { id: 'services_monitoring', label: 'Services Monitoring', component: <ServicesMonitoring />, permissionKey: null, icon: <Activity size={16} /> },
        { id: 'db_settings', label: 'DB Monitoring', component: <DbSettings />, permissionKey: null, icon: <Database size={16} /> },
        { id: 'configuration', label: 'Event Configuration', component: <ConfigurationSettings />, permissionKey: 'ticketing_event_configs', icon: <Wrench size={16} /> },
        // { id: 'jobs', label: 'Jobs', component: <Jobs />, permissionKey: 'jobs', icon: <Briefcase size={16} /> },
        { id: 'scheduler', label: 'Scheduler', component: <Schedules />, permissionKey: 'scheduler', icon: <CalendarDays size={16} /> },
        { id: 'tags', label: 'Tags', component: <TagsSettings />, permissionKey: 'tags', icon: <Tags size={16} /> },
        { id: 'users', label: 'Users', component: <User />, permissionKey: 'users', icon: <Users size={16} /> },
        { id: 'roles', label: 'Roles', component: <Roles />, permissionKey: 'roles', icon: <UserCog size={16} /> },

    ];

    const clientLevel = useClientLevel();

    const permittedTabs = useMemo(() => {
        return BASE_TABS.filter(tab => {
            // Check for level-based visibility
            if (tab.id === 'services_monitoring' && clientLevel.name === 'global') {
                return false;
            }

            if (tab.id === 'custom_scripts' && clientLevel.name === 'global') {
                return false;
            }

            if (tab.id === 'db_settings' && clientLevel.name === 'global') {
                return false;
            }


            if (tab.permissionKey) {
                return hasPermission(tab.permissionKey, 'read');
            }
            return true;
        });
    }, [clientLevel.name]);

    const requestedTab = searchParams.get('tab');
    const activeTab = useMemo(() => {
        const isValidTab = permittedTabs.some(tab => tab.id === requestedTab) || requestedTab === 'config_details';
        return isValidTab && requestedTab ? requestedTab : (permittedTabs[0]?.id || '');
    }, [requestedTab, permittedTabs]);

    useEffect(() => {
        if (permittedTabs.length > 0 && requestedTab !== activeTab) {
            setSearchParams({ tab: activeTab }, { replace: true });
        }
    }, [activeTab, requestedTab, permittedTabs, setSearchParams]);

    if (permittedTabs.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] bg-gray-900 text-gray-500">
                <Settings className="w-10 h-10 mb-4 opacity-50" />
                <p className="text-sm font-medium">You do not have permission to view settings.</p>
            </div>
        );
    }

    return (
        <div className="flex w-full bg-gray-900 text-gray-200 h-[calc(100vh-4rem)] overflow-hidden">
            {/* Sidebar - stays fixed, never scrolls */}
            <SettingsSidebar
                tabs={permittedTabs}
                activeTab={activeTab}
                onTabChange={(id) => setSearchParams({ tab: id })}
            />

            {/* Main Content - scrolls internally, sidebar unaffected */}
            <div className="flex-1 min-w-0 bg-gray-900 overflow-y-auto custom-scroll p-3">
                <div className="w-full mx-auto pb-12">
                    {activeTab === 'config_details' ? (
                        <ConfigurationDetails />
                    ) : (
                        permittedTabs.find(tab => tab.id === activeTab)?.component
                    )}
                </div>
            </div>
        </div>
    );
}
